/**
 * OrderCard — Trioty Orders card (grid/card view). Matches the design's card:
 *
 *   ┌──────────────────────────────────────┐
 *   │ #code                       EGP total │  ← header
 *   ├──────────────────────────────────────┤
 *   │ (avatar)  Customer name / city        │  ← customer
 *   ├──────────────────────────────────────┤
 *   │ [img]  Product name  +N more          │  ← product
 *   │ [Payment pill] [Status pill]  N items │  ← pills
 *   ├──────────────────────────────────────┤
 *   │ [ tinted next-step / View order btn ] │  ← action
 *   └──────────────────────────────────────┘
 *
 * Whole card → detail; the action button stops propagation.
 */

import { useCallback } from "react";
import type { OrderStatus, SellerOrderListItem } from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { formatCurrency } from "@/lib/formatting/currency";
import { pickLocalized } from "@/lib/i18n/localized";
import { ImageThumb } from "@/shared/components/PlaceholderImage";
import { getOrderItemImageUrl } from "@/lib/order-image";
import { useThemeStore } from "@/shared/theme/useTheme";
import { getStatusMeta } from "@/modules/orders/lib/order-status-meta";
import { getMoneyState } from "@/modules/orders/lib/order-money";
import { relativeAgo } from "@/modules/orders/lib/order-status-display";
import { useGovernorateName } from "./order-row/useGovernorateName";
import { actVisual, actColors, ActionGlyph, OrderStatusBadge } from "./order-row/order-visuals";
import { DraftWhatsAppLink } from "./orders-smart/DraftWhatsAppLink";

export type OrderCardProps = {
  order: SellerOrderListItem;
  onSelect: () => void;
  onQuickAction: (order: SellerOrderListItem, targetStatus: OrderStatus) => void;
  onOpenQuickSheet: (order: SellerOrderListItem) => void;
  busy: boolean;
  /** Hover-preview handlers for the floating order tooltip. */
  onHoverStart?: (e: React.MouseEvent) => void;
  onHoverMove?: (e: React.MouseEvent) => void;
  onHoverEnd?: () => void;
};

function productTitle(order: SellerOrderListItem, locale: Locale): string {
  const first = order.items[0];
  if (!first) return "—";
  return (
    pickLocalized(
      locale,
      first.resolvedTitleAr || first.productNameAr,
      first.resolvedTitleEn || first.productNameEn,
    ) ||
    first.resolvedTitleAr ||
    first.productNameAr
  );
}

export function OrderCard({
  order,
  onSelect,
  onQuickAction,
  onOpenQuickSheet,
  busy,
  onHoverStart,
  onHoverMove,
  onHoverEnd,
}: OrderCardProps) {
  const locale = useLocale();
  const t = useTranslations();
  const isDark = useThemeStore((s) => s.resolvedDark);
  const govName = useGovernorateName();

  const isDraft = order.status === "draft";
  const meta = getStatusMeta(order.status);
  const money = getMoneyState(
    order.status,
    order.paymentMethod,
    order.paidAmount,
    order.requiredAmount,
    order.totalAmount,
  );
  const ago = relativeAgo(order.createdAt, locale);
  const first = order.items[0] ?? null;
  const extra = order.itemCount - 1;
  const av = actVisual(order.status);
  const city = govName(order.governorateId) ?? order.buyerAddress ?? "";

  const runAction = useCallback(
    (o: SellerOrderListItem, targetStatus: OrderStatus) => {
      const primary = meta.transition;
      if (primary && (primary.needsEta || primary.requiresDeclineReason)) {
        onOpenQuickSheet(o);
      } else {
        onQuickAction(o, targetStatus);
      }
    },
    [meta.transition, onQuickAction, onOpenQuickSheet],
  );

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onSelect();
      }}
      onMouseEnter={onHoverStart}
      onMouseMove={onHoverMove}
      onMouseLeave={onHoverEnd}
      className={[
        "group flex cursor-pointer flex-col gap-3 rounded-[16px] px-[15px] py-[14px] transition-all",
        isDraft
          ? "border border-dashed border-[color:var(--dash)] bg-[color:var(--soft)] opacity-90 hover:opacity-100"
          : "border border-[color:var(--card-br)] bg-[color:var(--card)] hover:-translate-y-0.5 hover:border-[color:var(--pillring)] hover:shadow-[0_8px_22px_rgba(16,16,15,0.07)]",
      ].join(" ")}
    >
      {/* Header: #code + date (left) · total + payment (right) */}
      <div className="flex items-start justify-between gap-2.5">
        <div className="min-w-0">
          <div dir="ltr" className="truncate font-mono text-[13px] font-bold tracking-[0.02em] text-[color:var(--text-primary)]">
            #{order.orderCode}
          </div>
          {ago ? (
            <div className="mt-px text-[11.5px] text-[color:var(--text-muted)]">{ago}</div>
          ) : null}
        </div>
        <div className="shrink-0 text-end">
          <div className="text-[15px] font-bold text-[color:var(--text-primary)]">
            {formatCurrency(order.totalAmount, locale)}
          </div>
          {!isDraft ? (
            <div className="mt-0.5 flex items-center justify-end gap-[5px]">
              <span
                aria-hidden="true"
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: money.chipColor }}
              />
              <span className="text-[11.5px] font-semibold" style={{ color: money.chipColor }}>
                {t(money.chipKey)}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      {/* Customer: name + phone (same line) · city (no avatar) */}
      <div className="flex min-w-0 flex-col gap-[5px] border-t border-[color:var(--hair)] pt-3">
        <div className="flex items-center justify-between gap-2.5">
          <span className="min-w-0 truncate text-[13.5px] font-bold text-[color:var(--text-primary)]">
            {order.customer.name || "—"}
          </span>
          {order.customer.phoneE164 ? (
            <span dir="ltr" className="shrink-0 whitespace-nowrap text-[12px] tabular-nums text-[color:var(--text-muted)]">
              {order.customer.phoneE164}
            </span>
          ) : null}
        </div>
        {city ? (
          <div className="flex min-w-0 items-center gap-1.5 text-[12px] text-[color:var(--text-muted)]">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text-disabled)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0">
              <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <span dir="auto" className="truncate">{city}</span>
          </div>
        ) : null}
      </div>

      {/* Product: image + name + more */}
      <div className="flex min-w-0 items-center gap-[11px] border-t border-[color:var(--hair)] pt-3">
        <ImageThumb
          src={first ? getOrderItemImageUrl(first) : null}
          seed={first?.productId}
          alt={productTitle(order, locale)}
          size="h-[32px] w-[32px]"
          bg="bg-white"
          className="rounded-[9px]"
        />
        <div className="flex min-w-0 items-center gap-[7px]">
          <span className="truncate text-[12.5px] font-semibold text-[color:var(--text-primary)]">
            {productTitle(order, locale)}
          </span>
          {extra > 0 ? (
            <span className="shrink-0 whitespace-nowrap rounded-full bg-[color:var(--soft2)] px-[7px] py-px text-[11px] font-semibold text-[color:var(--text-muted)]">
              {t(keys.orders.rowMoreItems, { count: extra })}
            </span>
          ) : null}
        </div>
      </div>

      {/* Status + items count */}
      <div className="flex items-center justify-between gap-2">
        <OrderStatusBadge status={order.status} />
        <span className="shrink-0 text-[11.5px] text-[color:var(--text-muted)]">
          {t(keys.orders.itemsCountSummary, { count: order.itemCount })}
        </span>
      </div>

      {/* Action: tinted next-step, or a bordered "View order" (+ draft recovery) */}
      <div
        className="flex items-center gap-2 border-t border-[color:var(--hair)] pt-3"
        onClick={(e) => e.stopPropagation()}
      >
        {av && meta.transition ? (
          <button
            type="button"
            onClick={() => runAction(order, meta.transition!.targetStatus)}
            disabled={busy}
            className="inline-flex h-[38px] flex-1 items-center justify-center gap-[7px] rounded-[10px] border border-transparent text-[12.5px] font-bold transition-opacity hover:opacity-90 disabled:opacity-50"
            style={(() => {
              const c = actColors(av.tone, isDark);
              return { background: c.btnBg, color: c.btnFg };
            })()}
          >
            {busy ? (
              <svg aria-hidden="true" className="h-[15px] w-[15px] animate-spin" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeDashoffset="12" />
              </svg>
            ) : (
              <>
                <ActionGlyph kind={av.icon} />
                {t(meta.nextLabelKey)}
              </>
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={onSelect}
            className="inline-flex h-[38px] flex-1 items-center justify-center gap-[7px] rounded-[10px] border border-[color:var(--border-strong)] bg-[color:var(--card)] text-[12.5px] font-bold text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
          >
            {t(keys.orders.metaNextViewOrder)}
          </button>
        )}
        {isDraft ? (
          <DraftWhatsAppLink
            phoneE164={order.customer.phoneE164}
            customerName={order.customer.name || ""}
            variant="icon"
          />
        ) : null}
      </div>
    </article>
  );
}
